/** Resolve whether an address is a known genesis allocation pool. */

import { useEffect, useState } from 'react';

import {
	CPC_GENESIS_ALLOCATION_ROWS,
	vestingHint,
	vestingLabel,
	type CpcGenesisPoolRow,
} from '~/config/cpcGenesisTransparency';
import { tryNormalizeToHexAddress } from '~/utils/cpcAddress';

export type GenesisPoolMatch = CpcGenesisPoolRow & {
	vestingText: string;
	vestingDetail: string | null;
};

function normalizeHex(addr: string): string {
	try {
		return tryNormalizeToHexAddress(addr).toLowerCase();
	} catch {
		return addr.trim().toLowerCase();
	}
}

export function useGenesisPoolForAddress(address: string): {
	pool: GenesisPoolMatch | null;
	loading: boolean;
} {
	const [pool, setPool] = useState<GenesisPoolMatch | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		const target = normalizeHex(address);

		(async () => {
			setLoading(true);
			const byId: Record<string, string> = {};
			try {
				const res = await fetch('/cpc-genesis-allocations.json', { cache: 'no-store' });
				if (res.ok) {
					const doc = await res.json();
					for (const p of doc?.pools ?? []) {
						if (p?.id && p?.address) byId[String(p.id)] = String(p.address).trim();
					}
				}
			} catch {
				/* optional */
			}
			if (cancelled) return;

			for (const row of CPC_GENESIS_ALLOCATION_ROWS) {
				const addr = (byId[row.id] || row.address || '').trim();
				if (!addr) continue;
				if (normalizeHex(addr) === target) {
					setPool({
						...row,
						address: addr,
						vestingText: vestingLabel(row.vesting),
						vestingDetail: vestingHint(row.vesting),
					});
					setLoading(false);
					return;
				}
			}
			setPool(null);
			setLoading(false);
		})();

		return () => {
			cancelled = true;
		};
	}, [address]);

	return { pool, loading };
}
